import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from '../config';
import axios from 'axios';
import SharedNav from './SharedNav';
import { callNumber } from './shared/rcWidget';
import './TaskInbox.css';

const TASK_TYPE_LABELS = {
  drug_physical: 'Drug & Physical',
  physical_only: 'Physical Only',
  fadv_document: 'FADV Document',
  fadv_order_cancelled: 'Order Cancelled',
  referral: 'Referral',
  contact_candidate: 'Contact',
  interview_reminder: 'Interview Reminder',
  reschedule_followup: 'Reschedule',
  compliance_flag: 'Compliance/Safety',
  client_text_failed: 'Client Text',
  manual: 'Manual',
};

const TASK_TYPE_CSS = {
  drug_physical: 'drug-physical',
  physical_only: 'physical-only',
  fadv_document: 'fadv-doc',
  fadv_order_cancelled: 'order-cancelled',
  referral: 'referral',
  contact_candidate: 'contact',
  interview_reminder: 'interview-reminder',
  reschedule_followup: 'reschedule',
  compliance_flag: 'compliance',
  client_text_failed: 'client-text',
  manual: 'manual',
};

function getUrgency(task) {
  if (task.status === 'done') return 'done';
  if (task.status === 'snoozed') return 'snoozed';
  if (!task.due_date) return 'upcoming';
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date(task.due_date).toISOString().slice(0, 10);
  if (due < today) return 'overdue';
  if (due === today) return 'today';
  return 'upcoming';
}

function formatDueDate(task) {
  if (task.status === 'done') {
    if (!task.completed_at) return 'Completed';
    return `Done ${new Date(task.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  if (task.status === 'snoozed' && task.snoozed_until) {
    return `Snoozed until ${new Date(task.snoozed_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  if (!task.due_date) return '—';
  const d = new Date(task.due_date);
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function snoozeDate(option) {
  const d = new Date();
  if (option === 'tomorrow') d.setDate(d.getDate() + 1);
  else if (option === '3days') d.setDate(d.getDate() + 3);
  else if (option === '1week') d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

export default function TaskInbox({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const myId = String(user?.id || '');

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  // default: recruiters see their own tasks; admins see all
  const [filterRecruiter, setFilterRecruiter] = useState(isAdmin ? 'all' : myId);
  const [recruiters, setRecruiters] = useState([]);
  const [navTaskCount, setNavTaskCount] = useState(undefined);
  const [openSnooze, setOpenSnooze] = useState(null);
  const [customSnoozeDate, setCustomSnoozeDate] = useState('');
  const [openDisable, setOpenDisable] = useState(null);
  const [disableOtherText, setDisableOtherText] = useState('');
  const [matchTaskId, setMatchTaskId] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState(null);

  // Parse ?candidateId from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cid = params.get('candidateId');
    if (cid) setCandidateFilter(parseInt(cid));
  }, [location.search]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('taskType', filterType);
      if (filterDate !== 'all') params.append('dateFilter', filterDate);
      if (filterRecruiter) params.append('recruiterId', filterRecruiter);
      if (filterCompany !== 'all') params.append('companyId', filterCompany);
      const r = await axios.get(`${API_URL}/api/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(r.data);
    } catch (e) {
      console.error('fetchTasks error', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterDate, filterRecruiter, filterCompany, token]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Load recruiter list for both admins and recruiters
  useEffect(() => {
    axios.get(`${API_URL}/api/users/recruiters`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setRecruiters(r.data)).catch(() => {});
  }, [token]);

  // Load companies for company filter
  useEffect(() => {
    axios.get(`${API_URL}/api/companies`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setCompanies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [token]);

  // Keep nav badge in sync with current filter
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const params = new URLSearchParams();
        if (filterRecruiter) params.append('recruiterId', filterRecruiter);
        const r = await axios.get(`${API_URL}/api/tasks/count?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNavTaskCount(r.data.open || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCount();
  }, [filterRecruiter, token]);

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  // "Client Text" tasks: send the failed Send to Client texts again (routes/tasks.js).
  const [resending, setResending] = useState(null);
  const handleResendText = async (id) => {
    setResending(id);
    try {
      await axios.post(`${API_URL}/api/tasks/${id}/resend-client-text`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchTasks();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not resend the text.');
      fetchTasks();
    } finally { setResending(null); }
  };

  const handleSnooze = async (id, option) => {
    const presets = ['tomorrow', '3days', '1week'];
    const snoozedUntil = presets.includes(option) ? snoozeDate(option) : option;
    try {
      await axios.patch(`${API_URL}/api/tasks/${id}/snooze`, { snoozedUntil }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenSnooze(null);
      setCustomSnoozeDate('');
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  const handleUnsnooze = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${id}/unsnooze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  const handleMatchCandidate = async (taskId, candidateId) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${taskId}/match-candidate`, { candidateId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchTaskId(null);
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  const handleDisable = async (id, reason) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${id}/disable`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDisable(null);
      setDisableOtherText('');
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  // Close snooze dropdown on outside click
  const snoozeRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (snoozeRef.current && !snoozeRef.current.contains(e.target)) {
        setOpenSnooze(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close disable dropdown on outside click
  const disableRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (disableRef.current && !disableRef.current.contains(e.target)) {
        setOpenDisable(null);
        setDisableOtherText('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredTasks = candidateFilter
    ? tasks.filter(t => t.candidate_id === candidateFilter)
    : tasks;

  const overdue = filteredTasks.filter(t => getUrgency(t) === 'overdue').length;
  const today = filteredTasks.filter(t => getUrgency(t) === 'today').length;
  const upcoming = filteredTasks.filter(t => getUrgency(t) === 'upcoming').length;
  const snoozed = filteredTasks.filter(t => t.status === 'snoozed').length;

  return (
    <div className="task-inbox-page">
      <SharedNav user={user} onLogout={onLogout} taskCountOverride={navTaskCount} />

      <div className="ti-content">
        {/* Summary cards */}
        <div className="ti-summary-bar">
          {filterStatus === 'done' ? (
            <div className="ti-summary-card navy">
              <span className="ti-sum-num">{filteredTasks.length}</span>
              <span className="ti-sum-lbl">Completed</span>
            </div>
          ) : filterStatus === 'archived' ? (
            <div className="ti-summary-card gray">
              <span className="ti-sum-num">{filteredTasks.length}</span>
              <span className="ti-sum-lbl">Archived</span>
            </div>
          ) : (
            <>
              <div className="ti-summary-card red">
                <span className="ti-sum-num">{overdue}</span>
                <span className="ti-sum-lbl">Overdue</span>
              </div>
              <div className="ti-summary-card amber">
                <span className="ti-sum-num">{today}</span>
                <span className="ti-sum-lbl">Due Today</span>
              </div>
              <div className="ti-summary-card navy">
                <span className="ti-sum-num">{upcoming}</span>
                <span className="ti-sum-lbl">Upcoming</span>
              </div>
              <div className="ti-summary-card gray">
                <span className="ti-sum-num">{snoozed}</span>
                <span className="ti-sum-lbl">Snoozed</span>
              </div>
            </>
          )}

          <div className="ti-header-right">
            {candidateFilter && (
              <span className="ti-filter-chip">
                Filtered by candidate
                <button className="ti-chip-clear" onClick={() => { setCandidateFilter(null); navigate('/tasks'); }}>×</button>
              </span>
            )}
            <button className="ti-btn-primary" onClick={() => setShowCreateModal(true)}>+ New Task</button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="ti-filter-bar">
          <select className="ti-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="open">Open + Snoozed</option>
            <option value="archived">Task Archive</option>
            <option value="done">Completed</option>
            <option value="disabled">Disabled</option>
            <option value="all">All</option>
          </select>
          <select className="ti-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {Object.entries(TASK_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="ti-filter-select" value={filterDate} onChange={e => setFilterDate(e.target.value)}>
            <option value="all">All Dates</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due Today</option>
            <option value="this_week">This Week</option>
          </select>
          <select className="ti-filter-select" value={filterRecruiter} onChange={e => setFilterRecruiter(e.target.value)}>
            <option value="all">All Recruiters</option>
            {!isAdmin && <option value={myId}>Me</option>}
            {recruiters.filter(r => isAdmin || String(r.id) !== myId).map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <select className="ti-filter-select" value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="all">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Task table */}
        <div className="ti-table-wrap mtn-scroll-x">
          {loading ? (
            <div className="ti-empty">Loading tasks…</div>
          ) : filteredTasks.length === 0 ? (
            <div className="ti-empty">No tasks found.</div>
          ) : (
            <table className="ti-table">
              <thead>
                <tr>
                  <th style={{ width: 4 }}></th>
                  <th style={{ width: 130 }}>Type</th>
                  <th style={{ width: 190 }}>Candidate</th>
                  <th>Task</th>
                  <th style={{ width: 100 }}>Due</th>
                  <th style={{ width: 145 }}>Created</th>
                  {isAdmin && <th style={{ width: 120 }}>Recruiter</th>}
                  <th style={{ width: 185 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => {
                  const urgency = getUrgency(task);
                  const typeCss = TASK_TYPE_CSS[task.task_type] || 'manual';
                  const typeLabel = TASK_TYPE_LABELS[task.task_type] || task.task_type;
                  return (
                    <tr key={task.id} className={task.status === 'snoozed' ? 'ti-row-snoozed' : ''}>
                      <td className="ti-urgency-cell">
                        <div className={`ti-urgency-bar ${urgency}`} />
                      </td>
                      <td>
                        <span className={`ti-type-badge ${typeCss}`}>{typeLabel}</span>
                      </td>
                      <td>
                        {task.candidate_name ? (
                          <>
                            <div className="ti-cand-name">{task.candidate_name}</div>
                            <div className="ti-cand-sub">{task.company_name || '—'}</div>
                          </>
                        ) : task.fadv_email_log_id ? (
                          <>
                            <div className="ti-cand-unmatched">Unmatched</div>
                            <div className="ti-cand-sub">{task.unmatched_candidate_name || '—'}</div>
                            <div className="ti-cand-sub">{task.unmatched_company || '—'}</div>
                          </>
                        ) : <span className="ti-cand-sub">—</span>}
                      </td>
                      <td>
                        <div className="ti-task-title">{task.title}</div>
                        {task.description && <div className="ti-task-desc">{task.description}</div>}
                        {task.source === 'auto' && <div className="ti-task-source">Auto-triggered</div>}
                      </td>
                      <td>
                        <span className={`ti-due-date ${urgency}`}>{formatDueDate(task)}</span>
                      </td>
                      <td>
                        <span className="ti-created-date">
                          {task.created_at ? new Date(task.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td>
                          <span className="ti-recruiter-chip">{task.recruiter_name || '—'}</span>
                        </td>
                      )}
                      <td>
                        <div className="ti-action-group">
                          {task.candidate_phone && (
                            <button
                              type="button"
                              className="call-btn"
                              title={`Call ${task.candidate_name}`}
                              onClick={() => callNumber(task.candidate_phone)}
                            >
                              <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                            </button>
                          )}
                          {task.fadv_email_log_id && !task.candidate_id && (
                            <button className="mtn-btn mtn-btn-edit" onClick={() => setMatchTaskId(task.id)}>Match</button>
                          )}
                          {task.task_type === 'client_text_failed' && task.status !== 'done' && (
                            <button className="mtn-btn mtn-btn-edit" disabled={resending === task.id} onClick={() => handleResendText(task.id)}>{resending === task.id ? 'Sending…' : 'Resend text'}</button>
                          )}
                          {task.status !== 'done' && (
                            <button className="mtn-btn mtn-btn-send" onClick={() => handleComplete(task.id)}><span className="mtn-ic">✓</span>Done</button>
                          )}
                          {task.status === 'open' && (
                            <div className="ti-snooze-wrapper" ref={openSnooze === task.id ? snoozeRef : null}>
                              <button
                                className="mtn-btn mtn-btn-neutral"
                                onClick={() => setOpenSnooze(openSnooze === task.id ? null : task.id)}
                              >
                                Snooze ▾
                              </button>
                              {openSnooze === task.id && (
                                <div className="ti-snooze-dropdown">
                                  {[['tomorrow', 'Tomorrow'], ['3days', 'In 3 days'], ['1week', 'In 1 week']].map(([opt, lbl]) => (
                                    <div key={opt} className="ti-snooze-option" onClick={() => handleSnooze(task.id, opt)}>{lbl}</div>
                                  ))}
                                  <div className="ti-snooze-option ti-snooze-custom-row" onClick={e => e.stopPropagation()}>
                                    <input
                                      type="date"
                                      className="ti-snooze-date-input"
                                      value={customSnoozeDate}
                                      min={new Date().toISOString().slice(0, 10)}
                                      onChange={e => setCustomSnoozeDate(e.target.value)}
                                    />
                                    {customSnoozeDate && (
                                      <button className="ti-snooze-confirm-btn" onClick={() => handleSnooze(task.id, customSnoozeDate)}>Set</button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {task.status === 'snoozed' && (
                            <button className="mtn-btn mtn-btn-neutral" onClick={() => handleUnsnooze(task.id)}>Wake</button>
                          )}
                          {isAdmin && (
                            <div className="ti-disable-wrapper" ref={openDisable === task.id ? disableRef : null}>
                              <button
                                className="mtn-btn mtn-btn-delete"
                                onClick={() => { setOpenDisable(openDisable === task.id ? null : task.id); setDisableOtherText(''); }}
                              >
                                Disable ▾
                              </button>
                              {openDisable === task.id && (
                                <div className="ti-disable-dropdown">
                                  {['Not responding', 'Found another job', 'Not interested anymore'].map(reason => (
                                    <div key={reason} className="ti-disable-option" onClick={() => handleDisable(task.id, reason)}>{reason}</div>
                                  ))}
                                  <div className="ti-disable-other-row" onClick={e => e.stopPropagation()}>
                                    <input
                                      type="text"
                                      className="ti-disable-other-input"
                                      placeholder="Other reason…"
                                      value={disableOtherText}
                                      onChange={e => setDisableOtherText(e.target.value)}
                                    />
                                    {disableOtherText.trim() && (
                                      <button className="ti-disable-confirm-btn" onClick={() => handleDisable(task.id, disableOtherText.trim())}>Set</button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateTaskModal
          user={user}
          token={token}
          isAdmin={isAdmin}
          recruiters={recruiters}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); fetchTasks(); }}
        />
      )}

      {matchTaskId && (
        <MatchCandidateModal
          token={token}
          onClose={() => setMatchTaskId(null)}
          onMatch={(candidateId) => handleMatchCandidate(matchTaskId, candidateId)}
        />
      )}
    </div>
  );
}

function MatchCandidateModal({ token, onClose, onMatch }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/candidates?search=${encodeURIComponent(search)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = r.data.candidates || r.data;
        setResults(list.slice(0, 6));
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, token]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    await onMatch(selected.id);
    setSaving(false);
  };

  return (
    <div className="ti-modal-overlay" onClick={onClose}>
      <div className="ti-modal" onClick={e => e.stopPropagation()}>
        <div className="ti-modal-header">
          <h3>Match Candidate</h3>
          <button className="ti-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ti-modal-body">
          <div className="ti-form-group">
            <label>Search candidate</label>
            <input
              className="ti-form-input"
              type="text"
              placeholder="Type name, phone, or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null); }}
              autoFocus
            />
            {results.length > 0 && !selected && (
              <div className="ti-cand-dropdown">
                {results.map(c => (
                  <div key={c.id} className="ti-cand-option" onClick={() => { setSelected(c); setSearch(c.candidate_name); setResults([]); }}>
                    <strong>{c.candidate_name}</strong>
                    <span className="ti-cand-sub"> · {c.company_name || '—'}</span>
                  </div>
                ))}
              </div>
            )}
            {selected && (
              <div className="ti-cand-selected">✓ {selected.candidate_name} — {selected.company_name || '—'}</div>
            )}
          </div>
        </div>
        <div className="ti-modal-footer">
          <button className="ti-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="ti-btn-primary" onClick={handleConfirm} disabled={!selected || saving}>
            {saving ? 'Matching…' : 'Match & Apply Updates'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTaskModal({ user, token, isAdmin, recruiters, onClose, onCreated }) {
  const [taskType, setTaskType] = useState('manual');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateResults, setCandidateResults] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!candidateSearch.trim() || candidateSearch.trim().length < 2) {
      setCandidateResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/candidates?search=${encodeURIComponent(candidateSearch)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = r.data.candidates || r.data;
        setCandidateResults(list.slice(0, 5));
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [candidateSearch, token]);

  const handleSubmit = async () => {
    if (!title.trim()) { alert('Task title is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/tasks`, {
        candidateId: selectedCandidate?.id || null,
        taskType,
        title,
        description,
        dueDate: dueDate || null,
        assignedTo: assignedTo || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated();
    } catch (e) {
      console.error(e);
      alert('Error creating task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ti-modal-overlay" onClick={onClose}>
      <div className="ti-modal" onClick={e => e.stopPropagation()}>
        <div className="ti-modal-header">
          <h3>Create Task</h3>
          <button className="ti-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ti-modal-body">

          <div className="ti-form-group">
            <label>Task Type</label>
            <div className="ti-type-picker">
              {Object.entries(TASK_TYPE_LABELS).filter(([v]) => v !== 'client_text_failed').map(([v, l]) => (
                <button
                  key={v}
                  className={`ti-type-pill ${TASK_TYPE_CSS[v]}${taskType === v ? ' selected' : ''}`}
                  onClick={() => setTaskType(v)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="ti-form-group">
            <label>Candidate <span className="ti-optional">(optional)</span></label>
            <input
              className="ti-form-input"
              type="text"
              placeholder="Search by name…"
              value={candidateSearch}
              onChange={e => { setCandidateSearch(e.target.value); setSelectedCandidate(null); }}
            />
            {candidateResults.length > 0 && !selectedCandidate && (
              <div className="ti-cand-dropdown">
                {candidateResults.map(c => (
                  <div
                    key={c.id}
                    className="ti-cand-option"
                    onClick={() => { setSelectedCandidate(c); setCandidateSearch(c.candidate_name); setCandidateResults([]); }}
                  >
                    <strong>{c.candidate_name}</strong>
                    <span className="ti-cand-sub"> · {c.company_name || '—'}</span>
                  </div>
                ))}
              </div>
            )}
            {selectedCandidate && (
              <div className="ti-cand-selected">
                ✓ {selectedCandidate.candidate_name} — {selectedCandidate.company_name || '—'}
              </div>
            )}
          </div>

          <div className="ti-form-group">
            <label>Title</label>
            <input
              className="ti-form-input"
              type="text"
              placeholder="Task title…"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          <div className="ti-form-group">
            <label>Description <span className="ti-optional">(optional)</span></label>
            <textarea
              className="ti-form-textarea"
              placeholder="Add details…"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="ti-form-row">
            <div className="ti-form-group">
              <label>Due Date</label>
              <input
                className="ti-form-input"
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>
            {isAdmin && (
              <div className="ti-form-group">
                <label>Assign To</label>
                <select
                  className="ti-form-select"
                  value={assignedTo}
                  onChange={e => setAssignedTo(e.target.value)}
                >
                  <option value="">Me ({user.name || user.email})</option>
                  {recruiters.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

        </div>
        <div className="ti-modal-footer">
          <button className="ti-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="ti-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
